import type {
  JobCountRequestDto,
  JobCountResponseDto,
  JobFiltersRequestDto,
  JobFiltersResponseDto,
  JobsResponseDto,
} from "@artha/api-contracts";
import type { CreatorProfileForMyLink } from "@artha/types";
import {
  buildBoardSearchQueryParams,
  buildQueryString,
  createJobIndexClient,
  normalizeCategoriesParam,
} from "./client";
import type {
  FlexibleJobParams,
  JobBySlugItemDto,
  JobIndexFetchOptions,
  JobSuggestionDto,
} from "./client";

export type JobFeedParams = FlexibleJobParams;
export type FiltersParams = Partial<JobFiltersRequestDto>;
export type JobCountParams = Omit<JobCountRequestDto, "sort_by"> & {
  sort_by?: string;
  dedup_by?: string;
};
export type JobCountApiResponse = JobCountResponseDto;
export type PublicFiltersRequest = {
  include_categories?: boolean;
  include_job_types?: boolean;
  include_work_modes?: boolean;
  include_countries?: boolean;
  include_states?: boolean;
  include_cities?: boolean;
  include_experience_levels?: boolean;
  include_salary_ranges?: boolean;
  include_education_levels?: boolean;
  include_companies?: boolean;
  include_industries?: boolean;
};

export type JobApiRequestOptions = JobIndexFetchOptions;

export interface JobFetchersOptions {
  getBaseUrl: (baseUrl?: string) => string;
}

export function createJobFetchers({ getBaseUrl }: JobFetchersOptions) {
  const getJobClient = (baseUrl?: string) =>
    createJobIndexClient({ baseUrl: getBaseUrl(baseUrl) });

  async function fetchJobs(offset: number, params: JobFeedParams): Promise<JobsResponseDto>;
  async function fetchJobs(
    params?: JobFeedParams,
    options?: { creator?: CreatorProfileForMyLink }
  ): Promise<JobsResponseDto>;
  async function fetchJobs(
    offsetOrParams?: number | JobFeedParams,
    maybeParamsOrOptions?: JobFeedParams | { creator?: CreatorProfileForMyLink },
    maybeOptions?: { creator?: CreatorProfileForMyLink }
  ): Promise<JobsResponseDto> {
    const params: JobFeedParams =
      typeof offsetOrParams === "number"
        ? { ...(maybeParamsOrOptions as JobFeedParams), offset: offsetOrParams }
        : offsetOrParams || {};

    const creator =
      typeof offsetOrParams === "number"
        ? maybeOptions?.creator
        : (maybeParamsOrOptions as { creator?: CreatorProfileForMyLink })?.creator;

    return getJobClient().fetchRecommendJobs(
      params,
      { onlyEnriched: true, creator },
      { credentials: "include" }
    );
  }

  const fetchPublicJobs = async (
    username: string,
    params: JobFeedParams = {},
    options: JobApiRequestOptions & {
      creator?: CreatorProfileForMyLink;
      geoBoost?: boolean;
    } = {}
  ): Promise<JobsResponseDto> => {
    void username;
    const { creator, geoBoost = false, baseUrl, ...requestInit } = options;
    return getJobClient(baseUrl).fetchRecommendJobs(
      params,
      { creator, geoBoost, onlyEnriched: true },
      { credentials: "omit", ...requestInit }
    );
  };

  const fetchPublicJobCount = async (
    params: JobFeedParams = {},
    requestInit?: JobApiRequestOptions
  ): Promise<JobCountApiResponse> => {
    const { baseUrl, ...rest } = requestInit || {};
    const queryParams = buildBoardSearchQueryParams(params, { onlyEnriched: true });
    delete queryParams.limit;
    delete queryParams.offset;
    return getJobClient(baseUrl).fetchJobCount(queryParams, {
      credentials: "omit",
      ...(rest || {}),
    });
  };

  const fetchPublicJobFilters = async (
    params: PublicFiltersRequest = {},
    requestInit?: JobApiRequestOptions
  ): Promise<JobFiltersResponseDto> => {
    const { baseUrl, ...rest } = requestInit || {};
    const defaults: Required<PublicFiltersRequest> = {
      include_categories: true,
      include_job_types: true,
      include_work_modes: true,
      include_countries: false,
      include_states: false,
      include_cities: false,
      include_experience_levels: true,
      include_salary_ranges: true,
      include_education_levels: true,
      include_companies: false,
      include_industries: true,
    };

    return getJobClient(baseUrl).fetchFilters(
      { ...defaults, ...params },
      { credentials: "omit", ...(rest || {}) }
    );
  };

  const fetchSimilarJobs = async (
    jobSlug: string,
    limit = 10,
    query?: string,
    location?: string
  ): Promise<JobsResponseDto> => {
    return getJobClient().fetchSimilarJobs(jobSlug, limit, query, location ?? "US", {
      credentials: "omit",
    });
  };

  const fetchRecommendSearch = async (
    params: JobFeedParams,
    options: {
      sortBy?: string;
      dedupBy?: string;
      page?: number;
      limit?: number;
      creator?: CreatorProfileForMyLink;
    } = {}
  ): Promise<JobsResponseDto> => {
    const { sortBy, dedupBy, creator } = options;
    return getJobClient().fetchRecommendSearch(
      params,
      { sortBy, dedupBy, creator },
      { credentials: "omit" }
    );
  };

  const fetchJobSuggestions = async (
    query: string,
    limit = 10
  ): Promise<JobSuggestionDto[]> => {
    return getJobClient().fetchJobSuggestions(query, limit, { credentials: "omit" });
  };

  const fetchJobBySlug = async (
    jobSlug: string,
    requestInit?: JobApiRequestOptions
  ): Promise<JobBySlugItemDto | null> => {
    const { baseUrl, ...rest } = requestInit || {};
    return getJobClient(baseUrl).fetchJobBySlug(jobSlug, {
      credentials: "omit",
      ...(rest || {}),
    });
  };

  async function fetchFilters(params: FiltersParams = {}): Promise<JobFiltersResponseDto> {
    return getJobClient().fetchFilters(params, { credentials: "include" });
  }

  async function fetchJobCount(params: JobCountParams): Promise<JobCountApiResponse> {
    const queryParams = buildBoardSearchQueryParams(params, { onlyEnriched: true });
    queryParams.categories = normalizeCategoriesParam(params.categories) || "software-development";
    delete queryParams.limit;
    delete queryParams.offset;

    return getJobClient().fetchJobCount(queryParams, { credentials: "include" });
  }

  return {
    fetchJobs,
    fetchPublicJobs,
    fetchPublicJobCount,
    fetchPublicJobFilters,
    fetchSimilarJobs,
    fetchRecommendSearch,
    fetchJobSuggestions,
    fetchJobBySlug,
    fetchFilters,
    fetchJobCount,
    buildQueryString,
  };
}
